/**
 * Sortable table. Sorting is delegated to the server (the API allowlists sort
 * columns) so paging and ordering stay consistent across 5k+ rows.
 *
 * Every list that sorts by its column headings goes through here (or PagedTable,
 * below), so the headings, the row behaviour and the pager are written once.
 * `rowClass` marks a row by its record — the Error Log dims a resolved failure.
 */
import { Fragment, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, Empty, Loading, ErrorState, Pager, SortTh } from './ui'
import { useRelabel } from '../lib/terms'

export function DataTable({
  columns,
  rows,
  sort,
  dir,
  onSort,
  rowKey = (r) => r.id,
  rowTo,
  rowClass,
  compact,
  loading,
  error,
  refetch,
  empty,
  footer,
}) {
  const navigate = useNavigate()

  if (loading) {
    return (
      <div className="card">
        <div className="card-body">
          <Loading rows={6} />
        </div>
      </div>
    )
  }
  if (error) {
    return (
      <div className="card">
        <div className="card-body">
          <ErrorState error={error} retry={refetch} />
        </div>
      </div>
    )
  }

  return (
    <div className="table-wrap">
      <table className={`data${compact ? ' compact' : ''}`}>
        <thead>
          <tr>
            {columns.map((c) => (
              <SortTh
                key={c.key}
                label={c.label}
                sortKey={c.sortKey}
                sort={sort}
                dir={dir}
                onSort={onSort}
                style={c.width ? { width: c.width } : undefined}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {!rows?.length && (
            <tr>
              <td colSpan={columns.length}>{empty ?? <Empty title="No matching rows" />}</td>
            </tr>
          )}
          {rows?.map((r) => (
            <tr
              key={rowKey(r)}
              className={[rowTo && 'clickable', rowClass?.(r)].filter(Boolean).join(' ') || undefined}
              // A row that opens a record is a tab stop and opens on Enter, the way the
              // link it stands in for would. Keys pressed on a link or button inside a
              // cell are theirs, not the row's.
              tabIndex={rowTo ? 0 : undefined}
              onKeyDown={
                rowTo
                  ? (e) => {
                      if (e.key !== 'Enter' || e.target !== e.currentTarget) return
                      e.preventDefault()
                      navigate(rowTo(r))
                    }
                  : undefined
              }
              onClick={
                rowTo
                  ? (e) => {
                      // Real links and buttons inside cells win, and so does a drag that
                      // selected text: that is someone copying out of the row.
                      if (e.target.closest('a,button,input,label,select')) return
                      if (window.getSelection?.()?.toString()) return
                      navigate(rowTo(r))
                    }
                  : undefined
              }
            >
              {columns.map((c) => (
                <td key={c.key} className={c.numeric ? 'num' : c.cellClass}>
                  {c.render ? c.render(r) : r[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {footer}
    </div>
  )
}

/** Table + pager, the combination almost every list page wants. */
export const PagedTable = ({ data, onPage, onLimit, ...rest }) => (
  <DataTable
    {...rest}
    rows={data?.rows}
    footer={
      data ? (
        <Pager
          page={data.page}
          pages={data.pages}
          total={data.total}
          limit={data.limit}
          onPage={onPage}
          onLimit={onLimit}
        />
      ) : null
    }
  />
)

/**
 * The "Sort by" row of buttons a list of cards sorts with, having no column headings to
 * click. `options` is `[{ key, label }]`, the keys being what the API sorts by.
 */
export function SortBar({ options, sort, dir, onSort }) {
  const t = useRelabel()
  if (!options?.length) return null
  return (
    <div className="row row-tight small sortbar">
      <span className="subtle">Sort by</span>
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          className={`btn btn-sm${sort === o.key ? ' active' : ''}`}
          onClick={() => onSort(o.key)}
          aria-pressed={sort === o.key}
        >
          {t(o.label)}
          {sort === o.key ? <span className="sort-arrow">{dir === 'desc' ? '▼' : '▲'}</span> : null}
        </button>
      ))}
    </div>
  )
}

/**
 * The same page of rows as cards, two to a row — the other half of every list page that
 * offers both views.
 *
 * `card` renders one row; everything around it is the table's own furniture, taken from
 * the same `columns` the table is given. That is the point of passing them: sorting has
 * no column headers to live on here, so it becomes a bar of buttons built from the
 * fields the table lets you sort by, and the two views cannot drift apart into different
 * lists of them.
 */
export function PagedCards({
  data, loading, error, refetch, columns, sort, dir, onSort, onPage, onLimit,
  rowKey = (r) => r.id, card, empty,
}) {
  const sortable = columns.filter((c) => c.sortKey)

  if (loading) {
    return (
      <Card>
        <Loading rows={6} />
      </Card>
    )
  }
  if (error) {
    return (
      <Card>
        <ErrorState error={error} retry={refetch} />
      </Card>
    )
  }
  if (!data?.rows?.length) {
    return <Card>{empty ?? <Empty title="No matching rows" />}</Card>
  }

  return (
    <>
      <SortBar options={sortable.map((c) => ({ key: c.sortKey, label: c.label }))} sort={sort} dir={dir} onSort={onSort} />

      <div className="grid cards-2">
        {data.rows.map((r) => (
          <Fragment key={rowKey(r)}>{card(r)}</Fragment>
        ))}
      </div>

      <div className="card">
        <Pager
          page={data.page}
          pages={data.pages}
          total={data.total}
          limit={data.limit}
          onPage={onPage}
          onLimit={onLimit}
        />
      </div>
    </>
  )
}

/**
 * The Cards / Table switch, so the two views are offered in the same words and the same
 * order wherever a page has both — the default on the left, as everywhere else.
 */
/**
 * The Cards / Table choice, remembered per page under `s4n.view.<id>` — the same
 * bargain the filter panels strike under `s4n.filters.<id>`, and for the same reason:
 * someone who prefers the table on a page should not have to say so on every visit.
 *
 * Kept per page rather than one setting for the whole archive, because the two views
 * are not equally useful everywhere — the cards earn their place on a board of needs,
 * the table on a list of five thousand updates — and a page's own id is what the
 * filter panels already key on.
 *
 * Anything other than the two known values is ignored: a key edited by hand, or left
 * behind by an older build, should not leave a page rendering neither view.
 */
export function useViewMode(id, initial = 'cards') {
  const key = `s4n.view.${id}`
  const [view, setViewState] = useState(() => {
    try {
      const v = localStorage.getItem(key)
      return v === 'cards' || v === 'table' ? v : initial
    } catch {
      return initial
    }
  })

  const setView = (v) => {
    try {
      localStorage.setItem(key, v)
    } catch {}
    setViewState(v)
  }

  return [view, setView]
}

export const ViewToggle = ({ view, onView }) => (
  <div className="btn-group">
    <button className={`btn btn-sm${view === 'cards' ? ' active' : ''}`} onClick={() => onView('cards')}>
      Cards
    </button>
    <button className={`btn btn-sm${view === 'table' ? ' active' : ''}`} onClick={() => onView('table')}>
      Table
    </button>
  </div>
)
